import { useState, useEffect } from 'react';
import { translationsApi } from '../lib/api/translations';
import { cmsApi } from '../lib/api/cms';

type TranslationKey = string;
type Translations = Record<string, any>;

// Default translations from the existing website content
const defaultTranslations: Translations = {
  navigation: {
    home: "Avaleht",
    services: "Teenused",
    about: "Meie Ettevõte",
    contact: "Kontakt",
    contactUs: "Võta ühendust"
  },
  hero: {
    title: "Metsamajanduse eksperdid alates 2005",
    subtitle: "Kvaliteetsed teenused metsamaa majandamisel, raieõiguse ning metsamaa ostust ja müügist.",
    servicesButton: "Meie teenused",
    contactButton: "Võta ühendust",
    scrollText: "Loe rohkem"
  },
  services: {
    title: "Meie teenused",
    subtitle: "Pakume mitmekülgseid metsandusega seotud teenuseid, alates raieõiguse ja metsamaterjali ostust-müügist kuni maa ostu ja müügini.",
    readMore: "Loe rohkem",
    contactUs: "Võta ühendust",
    items: {
      "raieoiguse-ost": {
        title: "Raieõiguse ost",
        description: "Pakume kahte võimalust raieõiguse võõrandamiseks: hinnapakkumine konkreetsele raieõigusele (eeldab metsateatise olemasolu) või raieõiguse võõrandamine väljatulekuna, kus teostame metsa ülestöötamise ja transpordi.",
        fullDescription: "Ostame raieõigust kahel viisil:\n\n1. Esimene võimalus\n- Meie vaatame metsa üle mida on vaja majandada\n- Teeme omanikule ettepaneku kuidas erinevad metsaosad majandada\n- Tellime metsamajanduskava\n- Esitame metsateatised\n- Sõlmime omanikuga raieõiguse võõrandamise lepingu väljatuleku järgi\n- Teostame vajalikud raied, sealhulgas valgustusraie ja uue metsa istutamine lageraie aladele\n- Tööde edenemise käigus teeme omanikule ettemakseid\n- Kui tööd lõpetatud ja materjal realiseeritud, tasume omanikule lõpparve\n\n2. Teine võimalus - raieõiguse võõrandamine\n- Eraldistel mis lähevad raidesse peavad olema kehtivad metsateatised\n- Nende alusel lepitakse omanikuga kokku hind\n- Tasutakse korraga konkreetse raieõiguse eest\n- Kasutatakse üldjuhul lageraiete võõrandamise puhul, mitte harvendusraiel"
      },
      "metsamaterjali-ost": {
        title: "Metsamaterjali ost",
        description: "Ostame metsamaterjali - palke, paberipuitu ja küttepuid. Metsamaterjal peab olema sortimentide järgi eraldi riidastatud tee äärde, et metsaveok saaks koorma peale laadida.",
        fullDescription: "Ostame metsaümarmaterjali ka siis kui omanik on ise korraldanud raietöö ja väljaveo raielangilt tee äärde laoplatsi.\n\nEeldused ja tingimused:\n- Materjali sortimendid peavad olema õigesti lõigatud\n- Soovitame omanikul eelnevalt konsulteerida mõõtude osas\n- Materjal peab olema tee äärde riidastatud sortimentide kaupa\n- Vajalik kehtiva metsateatise olemasolu materjali päritolu kohta\n\nProtsess:\n- Saadame metsaveoauto peale laadima\n- Transport kas sadamasse või saeveskisse\n- Kui materjali kogused on selgunud, vormistame omanikuga metsamaterjali võõrandamise akti\n- Tasume raha Eesti pangakonto kaudu"
      },
      "hakkepuidu-ost-muuk": {
        title: "Hakkepuidu ost ja müük",
        description: "Ostame hakkepuitu. Osutame võsaraie kompleksteenust, näiteks kraavide, lagendike ja noorendike võsast puhastamist, korraldades kogu protsessi.",
        fullDescription: "Hakkepuit mis hõlmab nii tüvahaket kui raiejäätmeid ostame kahel viisil:\n\n1. Ülestöötamine meie poolt\n- Teostame kui omanik pöördub enne tööde alustamist\n- Pakume tüvahakke ülestöötamist giljotiiniga suuremate koguste puhul\n- Sobib kraavi kallaste puhastamiseks\n- Võsastunud ala puhastamiseks\n- Alusmetsa puhastamiseks\n\n2. Noorendike harvendamine\n- Teostame käsilõikusena\n- Saemees harvendab noorendikku\n- Koondab käsitsi maha raiutud tüved hunnikutesse\n- Hiljem veame välja ja realiseerime\n- Kallim töö, aga tulemus jääb hea ja noorendiku alune puhas\n- Sobib kui noorendiku tüvede keskmine rinnasdiameeter on alates 5cm\n- Alla 5cm harvendab mees noorendikku võsalõikuriga ja tüved jäävad maha\n\nLisaks ostame hakkepuitu mis omanik ise on tootnud ja tee äärde riidastanud."
      },
      "kuttepuude-muuk": {
        title: "Küttepuude müük",
        description: "3-meetriseid lehtpuu- ja okaspuuküttepuid Harjumaal aastaringselt. Küttepuude eelisteks on soodne hind ning kvaliteet.",
        fullDescription: "Müüme 3m pikkuseid küttepuid ja transpordime metsaveokiga tellijale koju üle kogu Harjumaa.\n\nTingimused:\n- Eeldab korralikku ligipääsu metsaveokile\n- Maksmine Eesti pangakonto kaudu\n- Hinnad sisaldavad 20% käibemaksu\n\nPakutavad puuliigid:\n- Lepp (kõige populaarsem küttepuu)\n- Kask (hea põlemisomadused)\n- Haab (soodne hind)\n\nHinnad alates 45 EUR/ruumimeeter (km 3m)"
      },
      "metsamaa-ost-muuk": {
        title: "Metsamaa ost ja müük",
        description: "Forestry OÜ ostab ja müüb metsamaad üle kogu Eesti, sealhulgas ka koos põllumaaga. Aitame korraldada metsa ostu-müügi protsessi, sealhulgas vajadusel hüpoteegi tasumist tehingu raames.",
        fullDescription: "Tegeleme metsamaa ostu ja müügiga üle kogu Eesti:\n\n1. Metsamaa ost\n- Kiire ja aus hindamine\n- Kohene arveldus Eesti pangakonto kaudu\n- Aitame dokumentatsiooni korrastamisel\n- Notariaalne tehingu vormistamine\n\n2. Metsamaa müük\n- Professionaalne hindamine\n- Turuhinna analüüs\n- Läbipaistev müügiprotsess\n- Turundustegevus\n\n3. Lisateenused\n- Metsa inventeerimine\n- Maa hindamine\n- Dokumentatsiooni korrastamine\n- Hüpoteegi tasumine tehingu raames\n- Juriidiline nõustamine\n\nTehingud toimuvad vastavalt Eesti Vabariigi seadustele"
      },
      "metsamaterjali-ulestootamine": {
        title: "Metsamaterjali ülestöötamine",
        description: "Pakume metsamaterjali ülestöötamise teenust harvesteriga ja käsilõikusena ning metsamaterjali väljaveo teenust. Lisaks pakume valgustusraie teenust.",
        fullDescription: "Pakume metsa ülestöötamise teenust nii harvesteriga kui käsilõikusena koos võsa ettelõikusega.\n\nTeenused:\n- Harvesteriga raiume:\n  - Lageraied\n  - Suurema rinnasdiameetriga harvendusraied\n  - Aegjärksed raied\n- Käsilõikusena:\n  - Peenemad harvendusraied\n  - Valgustusraied\n\nLisateenused:\n- Materjali väljavedu raielangilt\n- Metsaveokiga materjali vedu sadamasse ja saeveskisse\n- Ülestöötatud materjali ost\n- Raiejärkude planeerimine\n\nKvaliteet:\n- Teostame kõik raied kvaliteetselt\n- Vastavalt Eesti metsaseadusele\n- Vajalik kehtiva metsateatise olemasolu\n- Keskkonnasõbralikud töövõtted"
      },
      "metsa-istutamine": {
        title: "Metsa istutamine",
        description: "Pakume metsa istutamise teenust koos kvaliteetsete taimedega vastavalt Eesti metsanduse standarditele.",
        fullDescription: "Pakume metsa istutamise teenust üle kogu Eesti.\n\nTingimused ja protsess:\n- Kui omanik on eelnevalt meiega oma metsa majandanud, saame istutamise teenuse summa tema tuludest enne tasumist maha arvata\n- Kui omanik soovib lihtsalt tellida istutamise teenust, esitame arve\n- Istutamine hõlmab:\n  - Lageraie ala ettevalmistamine kultivaatoriga või lapimasinaga\n  - Kvaliteetsete potitaimede istutamine mulda\n  - 2000-3000 taime iga hektari kohta\n  - Taimede päritolu sertifikaat\n\nPiirangud:\n- Istutada ei saa võsastunud raielangile\n- Võsa tuleb eelnevalt eemaldada\n- Sobivad ilmastikutingimused\n\nHind:\n- Istutamise teenuse hind kõik kokku (koos tööde ja taimede maksumusega): 1400-1900 EUR/ha\n- Hinnad sisaldavad 20% käibemaksu"
      },
      "metsamaterjali-transport": {
        title: "Metsamaterjali transport",
        description: "Pakume metsamaterjali transportteenust Volvo metsaveokiga (koos haagisega ja palgi laadimise tõstukiga). Veetav materjal peab olema ladustatud vähemalt kruusatee äärde, riidastatud kujul.",
        fullDescription: "Pakume professionaalset metsamaterjali transportteenust üle kogu Eesti:\n\n1. Tehnika\n- Volvo metsaveok\n- Kaasaegne haagis\n- Palgi laadimise tõstuk\n- Professionaalne varustus\n- GPS jälgimine\n\n2. Nõuded materjalile\n- Ladustatud vähemalt kruusatee äärde\n- Korralikult riidastatud sortimentide kaupa\n- Ligipääsetav metsaveokile\n- Kehtiv metsateatise koopia\n\n3. Teenuse eelised\n- Kogenud ja sertifitseeritud autojuhid\n- Kiire teenindus\n- Paindlik graafik\n- Konkurentsivõimeline hind\n- Kindlustatud transport\n- Täpne arvestus\n\nTranspordi sihtkohad: sadamad, saeveskid, tselluloositehaseid"
      }
    }
  },
  about: {
    title: "Meie ettevõttest",
    subtitle: "Forestry OÜ on tegutsenud Eesti metsandussektoris aastast 2005, pakkudes klientidele kvaliteetseid teenuseid ja usaldusväärseid lahendusi kogu riigi territooriumil.",
    companyOverview: "Ettevõtte ülevaade",
    founded: "Asutatud",
    location: "Asukoht",
    phone: "Telefon",
    email: "E-post",
    boardMembers: "Juhatuse liikmed",
    yearsExperience: "Usaldusväärne partner Eesti metsandusvaldkonnas juba 20 aastat",
    companyInfo: {
      founded: "2005",
      address: "Osmussaare tänav 8, 13811 Tallinn, Eesti",
      phone1: "+372 6707 110",
      phone2: "+372 5663 6076",
      email: "info@forestryest.ee"
    },
    teamMembers: [
      {
        name: "Ain Meister",
        position: "Juhatuse liige",
        phone: "5663 6076",
        email: "ain@forestryest.ee"
      },
      {
        name: "Risto Esko",
        position: "Juhatuse liige",
        phone: "5663 8551",
        email: "risto@forestryest.ee"
      }
    ]
  },
  contact: {
    title: "Võta meiega ühendust",
    subtitle: "Kui Sul on küsimusi või soovid meie teenuste kohta rohkem teada saada, siis võta meiega julgelt ühendust. Vastame kõigile päringutele 24 tunni jooksul.",
    address: "Aadress",
    phone: "Telefon",
    email: "E-post",
    sendMessage: "Saada meile sõnum",
    businessHours: "Lahtiolekuajad: E-R 8:00-17:00",
    form: {
      name: "Nimi",
      namePlaceholder: "Sisesta oma nimi",
      email: "E-posti aadress",
      emailPlaceholder: "Sisesta oma e-posti aadress",
      subject: "Teema",
      subjectPlaceholder: "Sisesta sõnumi teema",
      message: "Sõnum",
      messagePlaceholder: "Kirjuta oma sõnum siia...",
      submit: "Saada sõnum",
      successMessage: "Täname, et võtsite meiega ühendust! Vastame teile esimesel võimalusel.",
      required: "Kohustuslik väli"
    },
    info: {
      address: "Osmussaare tänav 8, 13811 Tallinn, Eesti",
      phone: [
        "+372 6 707 110",
        "+372 5663 6076"
      ],
      email: "info@forestryest.ee"
    }
  },
  footer: {
    companyName: "Forestry OÜ",
    companyDescription: "Eesti metsandussektori juhtiv partner juba {years} aastat. Pakume kvaliteetseid teenuseid metsamaa ja metsamaterjali ostust-müügist kuni transportini üle kogu Eesti.",
    quickLinks: "Kiirlingid",
    services: "Teenused",
    contact: "Kontakt",
    legal: "Õiguslik teave",
    copyright: "© {year} Forestry OÜ. Kõik õigused kaitstud. Reg.nr 12345678",
    vatInfo: "Käibemaksukohustuslane. KMKR nr EE123456789",
    links: {
      company: [
        { name: "Avaleht", url: "/" },
        { name: "Teenused", url: "/teenused" },
        { name: "Meie Ettevõte", url: "/meist" },
        { name: "Kontakt", url: "/kontakt" }
      ],
      services: [
        { name: "Raieõiguse ost", url: "#raieoiguse-ost" },
        { name: "Metsamaterjali ost", url: "#metsamaterjali-ost" },
        { name: "Hakkepuidu ost ja müük", url: "#hakkepuidu-ost-muuk" },
        { name: "Küttepuude müük", url: "#kuttepuude-muuk" },
        { name: "Metsamaa ost ja müük", url: "#metsamaa-ost-muuk" },
        { name: "Metsamaterjali ülestöötamine", url: "#metsamaterjali-ulestootamine" },
        { name: "Metsa istutamine", url: "#metsa-istutamine" },
        { name: "Metsamaterjali transport", url: "#metsamaterjali-transport" }
      ],
      contact: [
        { name: "info@forestryest.ee", url: "mailto:info@forestryest.ee" },
        { name: "+372 6 707 110", url: "tel:+3726707110" },
        { name: "+372 5663 6076", url: "tel:+37256636076" },
        { name: "Osmussaare tänav 8, 13811 Tallinn", url: "/kontakt" }
      ],
      legal: [
        { name: "Privaatsuspoliitika", url: "/privaatsuspoliitika" },
        { name: "Kasutustingimused", url: "/kasutustingimused" },
        { name: "Küpsiste poliitika", url: "/kupsised" }
      ]
    }
  },
  breadcrumbs: {
    navigation: "Navigeerimine",
    home: "Avaleht"
  },
  social: {
    share: "Jaga",
    shareOn: "Jaga {platform}-is",
    shareViaEmail: "Jaga e-postiga"
  },
  legal: {
    lastUpdated: "Viimati uuendatud",
    privacyPolicy: {
      title: "Privaatsuspoliitika",
      dataController: "Andmetöötleja",
      dataProcessing: "Andmete töötlemine",
      rights: "Teie õigused",
      cookies: "Küpsised",
      contact: "Kontakt"
    },
    termsOfService: {
      title: "Kasutustingimused",
      general: "Üldised tingimused",
      services: "Teenused",
      pricing: "Hinnad",
      liability: "Vastutus",
      disputes: "Vaidluste lahendamine"
    }
  },
  common: {
    openMenu: "Ava menüü",
    closeMenu: "Sulge menüü",
    loading: "Laadimine...",
    error: "Viga",
    success: "Edukas",
    readMore: "Loe rohkem",
    showLess: "Näita vähem",
    backToTop: "Tagasi üles"
  },
  seo: {
    metaTitle: "Forestry OÜ - Metsanduse eksperdid Eestis | Metsamaa ost ja müük",
    metaDescription: "Eesti juhtiv metsandusettevõte. Metsamaa ost ja müük, metsamaterjali teenused, raieõiguse ost. Usaldusväärne partner alates 2005. aastast.",
    keywords: "metsamaa ost, metsamaa müük, metsamaterjali ost, raieõiguse ost, metsandus Eestis, küttepuud, hakkepuit, metsa istutamine, metsavedu"
  }
};

// Get nested value from object using dot notation
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

// Replace placeholders in string
const replacePlaceholders = (text: string, params: Record<string, any> = {}): string => {
  return text.replace(/\{(\w+)\}/g, (match, key) => {
    return params[key] !== undefined ? String(params[key]) : match;
  });
};

export const useDynamicTranslations = (locale: string = 'et') => {
  const [translationData, setTranslationData] = useState<Translations>({});
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load translations and team members in parallel
        const [translationsResult, teamResult] = await Promise.all([
          loadTranslations(),
          loadTeamMembers()
        ]);

        setTranslationData(translationsResult);
        setTeamMembers(teamResult);
      } catch (err) {
        console.error('Error loading data:', err);
        setTranslationData(defaultTranslations);
        setTeamMembers(defaultTranslations.about.teamMembers);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    loadData();

    // Listen for translation updates from CMS
    const handleTranslationUpdate = () => {
      loadData();
    };

    window.addEventListener('translationsUpdated', handleTranslationUpdate);
    return () => {
      window.removeEventListener('translationsUpdated', handleTranslationUpdate);
    };
  }, [locale]);

  const loadTranslations = async () => {
    try {
      // First, try to load from database
      const { data: dbTranslations, error } = await translationsApi.getTranslationsAsObject(locale);
      
      if (error) {
        console.warn('Failed to load translations from database, using defaults:', error);
        return defaultTranslations;
      }

      // If database is empty or missing keys, populate with defaults
      if (!dbTranslations || Object.keys(dbTranslations).length === 0) {
        console.log('Database translations empty, initializing with defaults...');
        await initializeTranslations();
        return defaultTranslations;
      } else {
        // Merge database translations with defaults (database takes precedence)
        return mergeTranslations(defaultTranslations, dbTranslations);
      }
    } catch (err) {
      console.error('Error loading translations:', err);
      return defaultTranslations;
    }
  };

  const loadTeamMembers = async () => {
    try {
      const { data: teamData, error } = await cmsApi.getTeamMembers();
      
      if (error || !teamData || teamData.length === 0) {
        // Return default team members if none in database
        return defaultTranslations.about.teamMembers;
      }

      // Transform database team members to match expected format
      return teamData.map((member: any) => ({
        name: member.name,
        position: member.position,
        phone: member.phone,
        email: member.email
      }));
    } catch (err) {
      console.error('Error loading team members:', err);
      return defaultTranslations.about.teamMembers;
    }
  };

  const initializeTranslations = async () => {
    try {
      await translationsApi.updateTranslationsFromObject(defaultTranslations, locale);
      console.log('Default translations initialized in database');
    } catch (error) {
      console.error('Failed to initialize translations:', error);
    }
  };

  const mergeTranslations = (defaults: any, database: any): any => {
    const result = { ...defaults };
    
    const merge = (target: any, source: any) => {
      Object.keys(source).forEach(key => {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          if (!target[key] || typeof target[key] !== 'object') {
            target[key] = {};
          }
          merge(target[key], source[key]);
        } else {
          target[key] = source[key];
        }
      });
    };

    merge(result, database);
    return result;
  };

  const t = (key: TranslationKey, params?: Record<string, any>): string => {
    const value = getNestedValue(translationData, key);
    
    if (value === undefined) {
      // Fallback to default translations
      const defaultValue = getNestedValue(defaultTranslations, key);
      if (defaultValue === undefined) {
        console.warn(`Translation key not found: ${key}`);
        return key;
      }
      
      if (typeof defaultValue === 'string') {
        return params ? replacePlaceholders(defaultValue, params) : defaultValue;
      }
      
      return key;
    }

    if (typeof value === 'string') {
      return params ? replacePlaceholders(value, params) : value;
    }

    if (typeof value === 'number') {
      return String(value);
    }

    console.warn(`Translation value is not a string or number: ${key}`, value);
    return key;
  };

  const getTranslation = (key: TranslationKey): any => {
    // Special handling for team members
    if (key === 'about.teamMembers') {
      return teamMembers;
    }

    const value = getNestedValue(translationData, key);
    if (value !== undefined) {
      return value;
    }
    
    // Fallback to defaults
    return getNestedValue(defaultTranslations, key);
  };

  const refreshTranslations = async () => {
    setIsLoading(true);
    try {
      const [translationsResult, teamResult] = await Promise.all([
        loadTranslations(),
        loadTeamMembers()
      ]);

      setTranslationData(translationsResult);
      setTeamMembers(teamResult);
    } catch (err) {
      console.error('Error refreshing translations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    t,
    getTranslation,
    isLoading,
    isInitialized,
    locale,
    refreshTranslations,
    defaultTranslations
  };
};

export default useDynamicTranslations;